import { config } from './config';
import type { ArticleCategoryRef, BlockNode, PageSection } from '../types/sections';
import type { TrackingCode } from './tags';
import { previewToken } from './preview';

export interface ApiRegion {
  code: string; name: string; domain: string; extraDomains: string[];
  defaultLocale: string; locales: { code: string; name: string }[];
  /**
   * This market's analytics and marketing tags. Empty - the default everywhere - means
   * nothing at all is loaded. Disabled entries never leave the CMS.
   */
  trackingCodes: TrackingCode[];
  newsletterEnabled: boolean;
}

/** Which articles a page is the detail template for. `none` is an ordinary page. */
export type ArticleLayoutFor = 'none' | 'all' | 'news' | 'blog';

/** True when what came back is a draft, because the request carried the preview token. */
export interface Previewable { preview?: boolean }

export interface ApiPageSummary {
  slug: string; title: string; locale: string; regionCode: string | null;
  isHomepage: boolean; isNewsIndex: boolean; articleLayoutFor: ArticleLayoutFor;
}

export interface ApiMedia { url: string; alternativeText?: string | null; width?: number; height?: number }

export interface ApiPage extends Previewable {
  slug: string; title: string; description: string | null; seoTitle: string;
  ogImage: ApiMedia | null; regionCode: string | null; locale: string;
  isHomepage: boolean; isNewsIndex: boolean; articleLayoutFor: ArticleLayoutFor;
  noindex: boolean;
  sections: PageSection[];
}

export type ArticleKind = 'news' | 'blog';

export interface ApiArticle {
  id: number; title: string; slug: string; excerpt: string | null;
  kind: ArticleKind; category: ArticleCategoryRef | null;
  publishedDate: string | null; cover: ApiMedia | null;
}

/** One article with everything the detail page needs. */
export interface ApiArticleDetail extends ApiArticle, Previewable {
  body: BlockNode[];
  seoTitle: string;
  seoDescription: string | null;
}

/** A category, with the number of published articles in it. */
export interface ApiArticleCategory extends ArticleCategoryRef { count: number }

/** Pagination, as `/website/articles` reports it. */
export interface ApiListMeta { page: number; pageSize: number; total: number; pageCount: number }

export interface ApiArticleQuery {
  category?: string;
  kind?: ArticleKind | 'all';
  /** Slugs to leave out, e.g. the article whose page the block is on. */
  exclude?: string[];
  page?: number;
  pageSize?: number;
}

export interface ApiNavCard {
  title: string; description: string | null; url: string | null;
  tag: string | null; tagTone: 'blue' | 'green'; image: ApiMedia | null;
}
export interface ApiNavLink { title: string; description: string | null; url: string | null }
export interface ApiNavGroup { title: string | null; links: ApiNavLink[] }
/**
 * One top-level menu item. `layout` picks the panel it opens - see `nav-item` in
 * tools/sections.spec.mjs - and an item whose panel would be empty is a plain link.
 */
export interface ApiNavItem {
  label: string;
  url: string | null;
  layout: 'cards' | 'columns' | 'wide';
  intro: { title: string | null; text: string | null; image: ApiMedia | null };
  groups: ApiNavGroup[];
  cards: ApiNavCard[];
}
export interface ApiNavigation { ctaLabel: string | null; ctaUrl: string | null; items: ApiNavItem[] }

export interface ApiFooterColumn { title: string | null; links: { label: string; url: string }[] }
export interface ApiFooter {
  heading: string | null;
  officesTitle: string | null;
  offices: { city: string; address: string; phone: string; email: string }[];
  /** Branches abroad, written into the footer entry - see the CMS component's comment. */
  abroadTitle: string | null;
  abroadOffices: { city: string; address: string | null; phone: string | null }[];
  columns: ApiFooterColumn[];
  social: { label: string; url: string }[];
  newsletterHeading: string | null;
  newsletterConsent: string | null;
  email: string | null;
  legalLinks: { label: string; url: string }[];
}

/** One switchable cookie category. `necessary` is not among them - it cannot be refused. */
export interface ApiConsentCategory { key: 'analytics' | 'marketing'; title: string; body: string | null }

/**
 * The cookie banner's wording, per region and language.
 *
 * `version` is what forces a fresh decision: change the text materially, bump it, and
 * everyone is asked again rather than being held to a consent they never read.
 */
export interface ApiConsent {
  version: string;
  heading: string | null;
  body: string;
  policyLabel: string | null;
  policyUrl: string | null;
  acceptAllLabel: string;
  rejectAllLabel: string;
  settingsLabel: string;
  saveLabel: string;
  necessaryTitle: string | null;
  necessaryBody: string | null;
  categories: ApiConsentCategory[];
}

/** The parts of the page that are not sections: the menu, the footer, the cookie banner. */
export interface ApiChrome {
  navigation: ApiNavigation | null;
  footer: ApiFooter | null;
  consent: ApiConsent | null;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

type Params = Record<string, string | number | undefined>;

/**
 * Endpoints that answer with a draft when the preview token is presented.
 *
 * A list rather than "send it on everything": the token is a bearer credential and every
 * request that carries it is another place it can leak. Only the endpoints that can return
 * unpublished content have any use for it.
 *
 * **It has to hold every one of them, and forgetting one fails quietly.** Landing pages
 * were added to the CMS side and not here, so the panel asked for a draft without the
 * token, got the published-only answer, and showed "not found" for an unpublished offer
 * that was perfectly complete. `pnpm verify` check [19] now compares this list against the
 * routes whose controller actually reads the token.
 */
const PREVIEWABLE = new Set(['/website/page', '/website/article', '/website/landing-page']);

async function getEnvelope<T>(path: string, params: Params = {}): Promise<{ data: T; meta?: unknown }> {
  const url = new URL(`${config.cmsUrl}/api${path}`);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const token = previewToken();
  if (token && PREVIEWABLE.has(path)) url.searchParams.set('preview', token);

  const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new ApiError(`GET ${path} failed`, res.status);
  return (await res.json()) as { data: T; meta?: unknown };
}

async function get<T>(path: string, params: Params = {}): Promise<T> {
  return (await getEnvelope<T>(path, params)).data;
}

/** Turn the article query options into flat query-string parameters. */
function articleParams(opts: ApiArticleQuery): Params {
  return {
    category: opts.category,
    kind: opts.kind && opts.kind !== 'all' ? opts.kind : undefined,
    exclude: opts.exclude?.length ? opts.exclude.join(',') : undefined,
    page: opts.page,
    pageSize: opts.pageSize
  };
}

export const api = {
  regions: () => get<ApiRegion[]>('/website/regions'),
  pages: (region: string, locale: string) => get<ApiPageSummary[]>('/website/pages', { region, locale }),
  page: (region: string, locale: string, slug: string) => get<ApiPage>('/website/page', { region, locale, slug }),
  // Its own endpoint, and deliberately not a flag on `page`: a landing page lives under
  // `/lp/` so an offer's address can never collide with a real page's, and two lookups
  // that cannot see each other's rows is what makes that true rather than merely likely.
  landingPage: (region: string, locale: string, slug: string) =>
    get<ApiPage>('/website/landing-page', { region, locale, slug }),
  articles: (region: string, locale: string, opts: ApiArticleQuery = {}) =>
    get<ApiArticle[]>('/website/articles', { region, locale, ...articleParams(opts) }),

  /** The same listing, with the pagination the article grid needs to draw its controls. */
  async articleList(region: string, locale: string, opts: ApiArticleQuery = {}) {
    const res = await getEnvelope<ApiArticle[]>('/website/articles', { region, locale, ...articleParams(opts) });
    const meta = (res.meta ?? {}) as Partial<ApiListMeta>;
    return {
      items: res.data,
      meta: {
        page: meta.page ?? 1,
        pageSize: meta.pageSize ?? res.data.length,
        total: meta.total ?? res.data.length,
        pageCount: meta.pageCount ?? 1
      } satisfies ApiListMeta
    };
  },

  articleCategories: (region: string, locale: string) =>
    get<ApiArticleCategory[]>('/website/article-categories', { region, locale }),
  article: (region: string, locale: string, slug: string) =>
    get<ApiArticleDetail>('/website/article', { region, locale, slug }),
  chrome: (region: string, locale: string) => get<ApiChrome>('/website/chrome', { region, locale }),
  contactDepartments: (region: string, locale: string) =>
    get<{ key: string; name: string }[]>('/website/contact-departments', { region, locale }),
  offices: (region: string) =>
    get<{ city: string; address: string; phone: string; email: string }[]>('/website/offices', { region }),

  async submitContactForm(payload: Record<string, unknown>): Promise<void> {
    const res = await fetch(`${config.cmsUrl}/api/website/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new ApiError(detail?.error?.message || 'submission failed', res.status);
    }
  },

  /**
   * Newsletter sign-up.
   *
   * Posted to this site's own API, never to the provider: the credential stays on the
   * server and no third-party script is loaded to collect an e-mail address. See
   * `apps/cms/src/api/website/services/newsletter.js`.
   */
  async subscribeNewsletter(payload: Record<string, unknown>): Promise<void> {
    const res = await fetch(`${config.cmsUrl}/api/website/newsletter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new ApiError(detail?.error?.message || 'sign-up failed', res.status);
    }
  }
};
